/**
 * What each plan actually gives you — one row per capability, one source.
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 *
 * The feature list was written out by hand in three places: the two plan cards
 * on /pricing/, the two on /, and the two on /ux-auditor/. Six hand-typed
 * arrays saying the same thing, with nothing keeping them in agreement. That
 * is the shape that produced "1 audit lifetime" surviving on a card after
 * backend/pricing.py had moved to one audit per MONTH: copy that was true when
 * it was typed, unreachable by the thing meant to keep it true.
 *
 * So the comparison table added below the cards does not get its own list.
 * The cards and the table read this file, and this file is pinned to the
 * backend by src/__tests__/plan-features-match-backend.test.js.
 *
 * ── EVERY ROW NAMES WHAT ENFORCES IT ────────────────────────────────────────
 *
 * `source` is not decoration. A capability row is a claim about the product,
 * and the rule this project keeps breaking is stating one that nothing checks.
 * If a row cannot name the file that enforces it, it does not belong here.
 *
 * PRICES ARE NEVER TYPED. Every figure interpolates from config/pricing.js,
 * which reads from backend/pricing.py. src/__tests__/no-bare-price-literal
 * sweeps this file like any other.
 */
// RELATIVE, not the '@/' alias. jest has no moduleNameMapper for '@/',
// so an aliased import here would make this file unrequirable from a
// guard -- and the guard that pins these rows to backend/pricing.py is
// the whole reason the file is worth having. Same directory either way.
import { PLAN_PRICES, ADDON_PRICES, CONTACT_TIERS } from './pricing';

/** Shown in the table when a plan does not include a capability at all. */
export const NOT_INCLUDED = '—';

/**
 * One row per capability.
 *
 *   label    the row header in the table, and the <th scope="row">
 *   free     what the free plan gives, as displayed
 *   starter  what the paid plan gives, as displayed
 *   bullet   { free, starter } — the card wording, or null to omit from that
 *            card. A card is a highlight reel; the table is the complete list.
 *   source   the file that enforces the row. Backend paths are relative to the
 *            repo root.
 *   agencySource
 *            OPTIONAL, and only where the Agency cell is backed by something
 *            other than `source`. Agency is a contact tier with no plan and no
 *            enforcement (see CONTACT_TIERS in config/pricing.js), so a cell
 *            that promises something no code does must say who does instead.
 *            `MANUAL:` is a real source: a person doing the thing IS the
 *            backing, and naming that is honest where a file path would lie.
 */
export const PLAN_FEATURE_ROWS = [
  {
    key: 'pages',
    label: 'Pages per audit',
    free: '1 page',
    starter: 'Up to 3 pages of the same site',
    agency: 'Up to 3 pages of the same site',
    bullet: { free: '1 page', starter: 'Up to 3 pages of the same site',
      agency: 'Up to 3 pages of the same site' },
    source: 'backend/audit/page_set.py (MAX_PAGES_FREE, MAX_PAGES_PAID)',
  },
  {
    key: 'quota',
    label: 'Audits per month',
    free: PLAN_PRICES.FREE.quota,
    // STARTER CARRIES THE SAME FREE AUDIT AS FREE, from 2026-09-01, and the
    // copy leads with it. `plan` records "has ever paid" and nothing resets
    // it, so the monthly audit shipped on 2026-08-31 reached every account
    // that had never paid and skipped every one that had. Reading only "no
    // monthly quota" would describe the absence of a cap and stay silent
    // about the audit the account now gets, which is the half worth knowing.
    starter: `${PLAN_PRICES.FREE.quota} free, then $${PLAN_PRICES.STARTER.usd} `
      + `${PLAN_PRICES.STARTER.quota} \u2014 no monthly cap`,
    agency: CONTACT_TIERS.AGENCY.quota,
    // On the card too, and no longer null. An addition to the paid tier, not
    // a limit on it: the free audit is what a past customer gains, and the
    // sentence has to read that way to a customer who has already paid us.
    bullet: { free: PLAN_PRICES.FREE.quota,
      starter: `Your ${PLAN_PRICES.FREE.quota} free, on top of anything you buy`,
      agency: CONTACT_TIERS.AGENCY.quota },
    source: 'backend/pricing.py (PLANS.FREE.monthly_audits, '
      + 'PLANS.STARTER.monthly_audits, PLANS.STARTER.pay_per_audit)',
  },
  {
    key: 'scores',
    label: 'Dimension scores',
    // BOTH tiers, and that is not a rounding of the truth: free_view withholds
    // the analysis PARAGRAPH for four of the six dimensions, never the score or
    // the issue count. Every number shows on a free report, which is the whole
    // point of showing it -- a withheld score reads as invented.
    free: 'Overall score and all 6',
    starter: 'Overall score and all 6',
    agency: 'Overall score and all 6',
    bullet: { free: 'Overall score and all 6 dimension scores', starter: null },
    source: 'backend/audit/free_view.py (score and issue count kept for all six)',
  },
  {
    key: 'findings',
    label: 'Findings detail',
    free: 'One finding in full, with its evidence, impact and fix',
    starter: 'Every finding with evidence and fix, severity prioritised',
    agency: 'Every finding with evidence and fix, severity prioritised',
    bullet: {
      free: 'One finding in full, with its evidence, impact and fix',
      starter: 'Every finding with evidence and fix',
      agency: 'Every finding with evidence and fix',
    },
    source: 'backend/audit/free_view.py (findings after index 0 reduced to a teaser)',
  },
  {
    key: 'crossPage',
    label: 'Cross-page analysis',
    // Not a paywall flag: the comparison pass needs more than one page, and a
    // free audit is capped at one. The limit produces the exclusion.
    free: NOT_INCLUDED,
    starter: 'Included',
    agency: 'Included',
    bullet: { free: null, starter: 'Cross-page analysis' },
    source: 'backend/audit/cross_page.py, gated by MAX_PAGES_FREE = 1',
  },
  {
    key: 'pdf',
    label: 'PDF export and sprint roadmap',
    free: NOT_INCLUDED,
    starter: 'Included',
    agency: 'Included',
    bullet: { free: null, starter: 'PDF export and sprint roadmap' },
    source: 'backend/server.py (403 "PDF export requires the paid report")',
  },
  {
    key: 'screenshots',
    label: 'Annotated screenshots',
    free: NOT_INCLUDED,
    starter: 'Included',
    agency: 'Included',
    bullet: { free: null, starter: 'Annotated screenshots' },
    source: 'backend/server.py (entitled -> signed_urls, blurred otherwise)',
  },
  {
    key: 'whiteLabel',
    label: 'White-label reports',
    free: NOT_INCLUDED,
    starter: `Optional add-on, $${ADDON_PRICES.white_label.usd} once`,
    agency: 'Included',
    bullet: { free: null, starter: `White-label branding +$${ADDON_PRICES.white_label.usd}`,
      agency: 'White Label included' },
    // agencySource for the same reason as the expert call below: on Starter the
    // add-on is priced in backend/pricing.py, on Agency it is included by hand.
    agencySource: 'MANUAL: owner commitment, no code path — white-label reports included on Agency, fulfilled by hand',
    source: 'backend/pricing.py (ADD_ONS.white_label), scoped per audit purchase',
  },
  {
    // Table only, no card bullet: the cards also render on / and
    // /ux-auditor/, which change behind a preview route (CLAUDE.md).
    // The day count is pinned to WINDOW_DAYS by
    // backend/tests/test_reaudit_claim_is_sourced.py.
    key: 'freeReaudit',
    label: 'Free re-audit',
    free: NOT_INCLUDED,
    starter: 'One per paid audit, same page, within 30 days',
    agency: 'One per paid audit, same page, within 30 days',
    bullet: { free: null, starter: null },
    source: 'backend/audit/reaudit.py (WINDOW_DAYS, pick_parent), granted in '
      + 'backend/server.py check_audit_limit',
  },
  {
    key: 'expertCall',
    label: '30-minute expert consultation',
    free: NOT_INCLUDED,
    starter: `Optional add-on, $${ADDON_PRICES.expert_call.usd} once`,
    // INCLUDED on Agency, one per month, at no extra charge — the tier's
    // differentiator, and it costs the owner time rather than code. Ruled
    // 2026-09-01.
    //
    // agencySource, not source: the row's `source` still correctly backs the
    // Free and Starter cells, where the add-on price IS read from
    // backend/pricing.py. The Agency cell is backed by a person doing the
    // call, so it names that instead — the same MANUAL: form as "priority
    // support", for the same reason. A file path invented here would claim
    // code enforces a monthly call. Nothing does.
    agency: 'Included, one per month',
    agencySource: 'MANUAL: owner commitment, no code path — one 30-minute call per month, fulfilled by hand',
    bullet: { free: null, starter: null, agency: '30-min expert call each month' },
    source: 'backend/pricing.py (ADD_ONS.expert_call)',
  },
];

/* THE CARD BULLETS THE TABLE DOES NOT CARRY.
   Two Free bullets and one Starter bullet are card-only: they are reassurance
   and ranking rather than a capability with a Free/Starter split, so a table
   row for them would read "Yes / Yes". They still live here, so the cards are
   still one list. */
export const CARD_ONLY = [
  { plan: 'free', text: 'No credit card required',
    source: 'backend/pricing.py (PLANS.FREE.usd is zero)' },
  { plan: 'starter', text: 'Severity prioritisation',
    source: 'backend/audit/report_format.py (_SEV_ORDER)' },
  // "Priority support" was here, backed only by MANUAL. Dropped (owner,
  // 2026-10-07): nothing defines what it is priority over.
];

/** The bullets for one plan card, in row order. `plan` is 'free' | 'starter' | 'agency'. */
export function cardBullets(plan) {
  return [
    ...PLAN_FEATURE_ROWS.map((r) => r.bullet[plan]).filter(Boolean),
    ...CARD_ONLY.filter((c) => c.plan === plan).map((c) => c.text),
  ];
}

/* THE PLAN CARDS, the one list /pricing/ and /ux-auditor/ both render.
   Owner, 2026-10-07: /ux-auditor/ showed two plans and /pricing/ three, because
   each page kept its own copy. Prices come from PLAN_PRICES / CONTACT_TIERS and
   bullets from cardBullets, so a card cannot say what the table does not.
   Agency is not a plan in code (see CONTACT_TIERS): `contact: true` makes its
   CTA a link to /contact/ on every page, never a checkout. */
export const PLAN_CARDS = [
  {
    name: 'Free',
    price: `$${PLAN_PRICES.FREE.usd}`,
    period: '',
    type: 'One audit every month',
    features: cardBullets('free'),
    highlighted: false,
  },
  {
    name: 'Starter',
    price: `$${PLAN_PRICES.STARTER.usd}`,
    period: '/audit',
    type: 'Pay per audit, no subscription',
    badge: 'MOST TEAMS',
    features: cardBullets('starter'),
    highlighted: true,
  },
  {
    name: 'Agency',
    price: `$${CONTACT_TIERS.AGENCY.usd}`,
    period: CONTACT_TIERS.AGENCY.period,
    type: 'White label, by arrangement',
    features: cardBullets('agency'),
    highlighted: false,
    contact: true,
  },
];
